import { html, unsafeCSS } from 'lit';
import { property, state, customElement } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { KeyboardKeyEvents } from '@trp-dms-digital-shared/ds-ui-library-common';

import { PopBaseComponent } from '../../base/pop-base-component';
import { PredictiveSearchService } from '@trp-dms-digital-shared/ds-ui-library-api';

import style from './summary-search.scss?inline';
import { SearchResult } from './summary-search.model';
import { Constants } from './summary-search.constants';
import { sanitizeHTML } from '../../common/utils/sanitizeHTML';

const NUMBER_OF_RESULTS_TO_SHOW = 5;

/**
 * @tag pop-summary-search-component
 */
@customElement(Constants.TAG_NAME)
export class PopSummarySearchComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  @state() searchInputValue = '';

  /**
   * @internal
   */
  @state() searchResults: SearchResult[] = [];

  /**
   * @internal
   */
  @state() showResults = false;

  /**
   * @internal
   */
  private subscription: Subscription | null = null;

  /**
   * @internal
   */
  private predictiveSearchService: PredictiveSearchService;

  /**
   * Input placeholder text
   */
  @property({
    attribute: Constants.DATA_SUMMARY_SEARCH_PLACEHOLDER,
    type: String,
  })
  placeholder = '';

  constructor() {
    super();
  }

  connectedCallback() {
    this.predictiveSearchService = new PredictiveSearchService();
    this.subscription = this.predictiveSearchService
      .fetch()
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((data) => this.handleSearchData(data));
    super.connectedCallback();
    document.addEventListener(
      Constants.CLICK,
      this.handleClickOutside.bind(this),
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    document.removeEventListener(
      Constants.CLICK,
      this.handleClickOutside.bind(this),
    );
  }

  private handleSearchData(data) {
    if (data) {
      const searchResultsRaw = data.subscriptionSearchResult;
      this.searchResults = searchResultsRaw
        .filter((result) => result.name)
        .slice(0, NUMBER_OF_RESULTS_TO_SHOW);
      this.showResults = true;
    } else {
      this.searchResults = [];
      this.showResults = false;
    }
  }

  handleClickOutside(event: MouseEvent) {
    const targetElement = event.target;
    const searchResultsList = this.shadowRoot?.querySelector(
      '.search-results-list',
    );
    if (
      targetElement instanceof HTMLElement &&
      this.showResults &&
      searchResultsList &&
      searchResultsList.children.length !== 0 &&
      !searchResultsList.contains(targetElement)
    ) {
      const input: HTMLElement | null | undefined =
        this.shadowRoot?.querySelector(Constants.SUMMARY_SEARCH_INPUT);
      if (input) {
        input.focus();
      }
    }
    this.showResults = false;
  }

  /**
   * To avoid XSS from user inputs
   * @param string
   * @returns
   */
  private escapeRegExp(string: string) {
    return string.replace(
      /([.*+?^=!:{}()$|[\]/\\])/g,
      Constants.FIRST_CAPTURE_GROUP,
    );
  }

  /**
   * Check the url environment
   * @param url
   * @returns
   */
  private isAuthorUrl(url: string): boolean {
    return (
      url.indexOf(Constants.LOCAL_HOST_450) !== -1 ||
      url.indexOf(Constants.AUTHOR_TROWEPRICE) !== -1 ||
      url.indexOf(Constants.AEM_AUTHOR) !== -1
    );
  }

  handleSearchItemKeyDown(e) {
    const searchResultsList = this.shadowRoot?.querySelector(
      Constants.SEARCH_RESULTS,
    );
    const searchResultsLinkList = searchResultsList?.querySelectorAll(
      Constants.SEARCH_RESULTS_LINK,
    );

    let lastSearchResultItem;
    if (searchResultsLinkList?.length) {
      lastSearchResultItem =
        searchResultsLinkList[searchResultsLinkList.length - 1];
    }

    if (
      lastSearchResultItem &&
      e.key === KeyboardKeyEvents.TAB &&
      !e.shiftKey
    ) {
      if (e.target === lastSearchResultItem) {
        this.showResults = false;
      }
    } else if (e.key === KeyboardKeyEvents.ESCAPE) {
      this.showResults = false;
      const input: HTMLElement | null | undefined =
        this.shadowRoot?.querySelector(Constants.SUMMARY_SEARCH_INPUT);
      if (input) {
        input.focus();
      }
    }
  }

  fetchData(inputValue: string) {
    this.predictiveSearchService.fetchData(inputValue);
  }

  onInputChange(event): void {
    if (event) {
      if (event.target.value.length > 2) {
        this.searchInputValue = event.target.value;
        this.fetchData(this.searchInputValue);
      } else {
        this.searchResults = [];
        this.showResults = false;
      }
    }
  }

  getBasePath() {
    return this.isAuthorUrl(window.location.host)
      ? Constants.CONTENT_FAI
      : Constants.FINANCIAL_INTERMEDIARY;
  }

  handleKeyDown(e) {
    if (e.key === KeyboardKeyEvents.TAB && e.shiftKey) {
      this.showResults = false;
    } else if (e.key === KeyboardKeyEvents.ENTER) {
      e.preventDefault();
      const inputValue = this.escapeRegExp(e.target.value);
      const basePath = this.getBasePath();
      const url = `${basePath}/us/en/search.html?q=${inputValue}`;
      window.open(sanitizeHTML(url), Constants.SELF);
    }
  }

  getCustomInputField() {
    return html`
      <input
        value=""
        class="summary-search-input b-typography-sans-serif--body-loose--100 b-font-weight-light ${this
          .showResults
          ? 'is-open'
          : ''}"
        @input="${this.onInputChange}"
        @keydown="${this.handleKeyDown}"
        placeholder="${this.placeholder}"
      />
    `;
  }

  getLabelWithBoldedSections(label: string, inputValue: string) {
    const regex = new RegExp(`(${inputValue})`, 'gi');
    const modifiedLabel = label.replace(
      regex,
      '<span class="b-typography-sans-serif--body--100 b-font-weight-bold">$1</span>',
    );
    return html`<div>${unsafeHTML(modifiedLabel)}</div>`;
  }

  getSearchResultLink(product: SearchResult) {
    const url = `${product.url}.html`;
    const label: string = product.name || '';

    return html`
      <a
        class="search-results-link b-typography-sans-serif--body-loose--100"
        href="${url}"
        target="_blank"
        rel="noopener noreferrer"
        @keydown="${(e) => this.handleSearchItemKeyDown(e)}"
      >
        ${this.getLabelWithBoldedSections(label, this.searchInputValue)}
      </a>
    `;
  }

  renderSearchOverlay() {
    return html` <div
      class="search-results ${!this.showResults ? 'results-hidden' : ''}"
    >
      <div class="summary-overlay-spacer"></div>
      <ul class="search-results-list">
        ${this.searchResults.map(
          (result) => html`
            <li class="search-results-item">
              ${this.getSearchResultLink(result)}
            </li>
          `,
        )}
      </ul>
    </div>`;
  }

  render() {
    return this.renderComponent(html`
      <div class="summary-search-container">
        ${this.getCustomInputField()} ${this.renderSearchOverlay()}
      </div>
    `);
  }
}
